import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';

// Store scroll positions for different routes
const scrollPositions = new Map<string, number>();

/**
 * Hook to manage scroll restoration behavior:
 * - Scrolls to top on new navigation
 * - Restores scroll position when going back/forward
 */
export function useScrollRestoration() {
  const [location] = useLocation();
  const previousLocation = useRef<string>('');
  const isNavigatingBack = useRef<boolean>(false);

  useEffect(() => {
    const handlePopState = () => {
      // Browser back/forward navigation
      isNavigatingBack.current = true;
    };

    // Listen for back/forward button navigation
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    // Save scroll position of previous route
    if (previousLocation.current) {
      scrollPositions.set(previousLocation.current, window.scrollY);
    }

    if (isNavigatingBack.current) {
      // Restore scroll position for back navigation
      const savedPosition = scrollPositions.get(location);
      if (savedPosition !== undefined) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          window.scrollTo(0, savedPosition);
        }, 0);
      }
      isNavigatingBack.current = false;
    } else {
      // Scroll to top for new navigation
      window.scrollTo(0, 0);
    }

    previousLocation.current = location;
  }, [location]);
}